import { Constants } from "../common";
import { IContents, IFolder, ISubscription } from "../models";

export class CommonFunctions {

    static convertTo12HourFormat(time: string): string {
        if (!time || typeof time !== 'string' || !time.includes(':')) {
            return ''; // Or handle as you wish (maybe return time or throw error)
        }
        const [hour, minute] = time.split(':');
        const date = new Date();
        date.setHours(parseInt(hour), parseInt(minute));

        const options: Intl.DateTimeFormatOptions = {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        };

        return new Intl.DateTimeFormat('en-US', options).format(date);  // Converts to 12-hour format
    }

    static convertTo24HourFormat(time: string): string {
        const [timePart, modifier] = time.split(' ');
        const [hourStr, minutes] = timePart.split(':');

        const hours = modifier === 'PM' && hourStr !== '12' ? (parseInt(hourStr, 10) + 12).toString() : (modifier === 'AM' && hourStr === '12' ? '00' : hourStr);

        return `${hours}:${minutes}:00`;
    }


    static convertSecondsToHHMMSS(seconds: number) {
        if (isFinite(seconds)) {
            // Calculate hours, minutes, and seconds
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;

            // Pad the values to ensure two-digit format
            const hoursStr = String(hours).padStart(2, '0');
            const minutesStr = String(minutes).padStart(2, '0');
            const secondsStr = String(secs).padStart(2, '0');

            return `${hoursStr}:${minutesStr}:${secondsStr}`;
        }
        else {
            return '00:00:00';
        }
    }

    static convertToUTC(dateString: string | null): string {
        const date = dateString ? new Date(dateString) : new Date();
        return date.toISOString(); // Converts to UTC in ISO 8601 format
    }


    static generateReadableTimestampName(): string {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${year}${month}${day}_${hours}${minutes}${seconds}`;
    }

    static mapFiles(contents: IContents[] = []) {
        return contents.map((file) => ({
            id: file.id,
            name: file.title + (file.file_format ? `${file.file_format == Constants.fileFormats.app ? '' : file.file_format}` : ''),
            isFolder: !file.file_format,
            thumbnail: file.thumbnail || '',
            file_size: file.file_size || 0,
            file_duration: file.file_duration || '',
            file_format: file.file_format || '',
            file_upload: file.file_upload || '',
            created_at: file.created_at,
            updated_at: file.updated_at,
            app_type: file.app_type,
            category: file.category,
            totalFolderSize: 0,
            totalMediacount: 0,
            processing_status: file.processing_status || null,
            resolution: file.resolution || null
        }));
    }

    static createFolderObject(folder: IFolder) {
        return {
            id: folder.id,
            name: folder.name,
            isFolder: true,
            thumbnail: folder.thumbnail || '',
            file_size: 0,
            file_duration: '',
            file_format: '',
            file_upload: '',
            app_type: '',
            category: '',
            created_at: folder.created_at,
            updated_at: folder.updated_at,
            totalFolderSize: folder.total_size || 0,
            totalMediacount: folder.media_count || 0,
            processing_status: null,
            resolution: null
        };
    }

    static getDaysSinceBusinessCreated(business_created_at: string) {
        const createdDate = new Date(business_created_at);
        const currentDate = new Date();
        const diffTime = currentDate.getTime() - createdDate.getTime();
        const diffDays = diffTime / (1000 * 3600 * 24);
        return diffDays;
    }

    static convertFileToContent(file: File): Promise<IContents> {
        return new Promise((resolve) => {
            const currentContent = {} as IContents;
            currentContent.uploading = false;
            currentContent.progress = 0;
            currentContent.uploadComplete = false;

            if (file) {
                currentContent.file = file;
                currentContent.title = file.name.substring(0, file.name.lastIndexOf('.'));
                currentContent.file_size = file.size;

                const fileFormat = this.resolveFileMimeType(file);
                const format = fileFormat.split('/').pop();
                const fileType = fileFormat ? fileFormat.split('/')[0] : '';
                currentContent.file_format = format ? format : '';
                currentContent.category = fileType;
                currentContent.source = Constants.applicationName;

                if (fileFormat.startsWith('image/')) {
                    const img = new Image();
                    img.onload = () => {
                        currentContent.resolution = img.width + 'x' + img.height;
                        currentContent.file_duration = "";
                        resolve(currentContent);
                    };
                    img.src = URL.createObjectURL(file);

                } else if (fileFormat.startsWith('video/')) {
                    const video = document.createElement('video');
                    const objectUrl = URL.createObjectURL(file);
                    let isResolved = false;

                    const finishVideoMetadata = (resolution = '', duration = '') => {
                        if (isResolved) return;
                        isResolved = true;
                        window.clearTimeout(metadataTimeout);
                        URL.revokeObjectURL(objectUrl);
                        currentContent.resolution = resolution;
                        currentContent.file_duration = duration;
                        resolve(currentContent);
                    };

                    video.onloadedmetadata = () => {
                        const resolution = video.videoWidth && video.videoHeight
                            ? `${video.videoWidth}x${video.videoHeight}`
                            : '';
                        const duration = Number.isFinite(video.duration)
                            ? Math.floor(video.duration).toString()
                            : '';
                        finishVideoMetadata(resolution, duration);
                    };
                    // Ubuntu browsers may not decode HEVC/H.265 metadata even though the
                    // MP4 is valid and can be uploaded. Metadata failure must not block upload.
                    video.onerror = () => finishVideoMetadata();
                    const metadataTimeout = window.setTimeout(() => finishVideoMetadata(), 5000);
                    video.preload = 'metadata';
                    video.src = objectUrl;
                    video.load();

                } else {
                    currentContent.resolution = '';
                    currentContent.file_duration = "";
                    resolve(currentContent);
                }
            } else {
                resolve(currentContent);
            }
        });
    }

    static resolveFileMimeType(file: File): string {
        const browserMimeType = file.type?.trim().toLowerCase();
        if (browserMimeType && browserMimeType !== 'application/octet-stream') {
            return browserMimeType;
        }

        const extension = file.name.split('.').pop()?.toLowerCase();
        const mimeTypesByExtension: Record<string, string> = {
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            mp4: 'video/mp4',
            pdf: 'application/pdf',
            pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        };

        return extension ? (mimeTypesByExtension[extension] || 'application/octet-stream') : 'application/octet-stream';
    }


    static validateFile(file: File): boolean {
        const allowedExtensions = Constants.fileExtensionRegex;
        return allowedExtensions.test(file.name);
    }

    static getTrialsRemainingDays(subscription: ISubscription) {
        const expiryDate = new Date(subscription.expiry_date);
        const currentDate = new Date();

        // Calculate the difference in time (milliseconds)
        const timeDiff = expiryDate.getTime() - currentDate.getTime();

        // Convert to days (rounding down)
        const remainingTrialDays = Math.max(Math.floor(timeDiff / (1000 * 60 * 60 * 24)), 0);

        return remainingTrialDays;
    }

    static getTrialsRemainingMinutes(subscription: ISubscription) {
        const expiryDate = new Date(subscription.expiry_date);
        const currentDate = new Date();

        // Calculate the difference in time (milliseconds)
        const timeDiff = expiryDate.getTime() - currentDate.getTime();

        // Convert to minutes (rounding down)
        const remainingTrialMinutes = Math.max(Math.floor(timeDiff / (1000 * 60)), 0);

        return remainingTrialMinutes;
    }

    static openScreenPreviewPopup(url: string, orientation: string) {
        const isDarkMode = document.body.classList.contains('dark-mode');
        const isLandscape = orientation === Constants.screenOrientations[0].value;

        const overlay = document.createElement('div');
        overlay.className = `screen-preview-overlay${isDarkMode ? ' screen-preview-overlay--dark' : ''}`;

        const style = document.createElement('style');
        style.innerHTML = `
            .screen-preview-overlay {
                position: fixed;
                inset: 0;
                width: 100vw;
                height: 100vh;
                z-index: 10000;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 24px;
                background: rgba(15, 23, 42, 0.38);
                backdrop-filter: blur(3px);
                box-sizing: border-box;
            }

            .screen-preview-popup {
                position: relative;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                background: #ffffff;
                border: 1px solid rgba(226, 232, 240, 0.95);
                border-radius: 14px;
                box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
            }

            .screen-preview-popup--landscape {
                width: min(92vw, 160vh);
                height: auto;
                max-height: 92vh;
                aspect-ratio: 16 / 9;
            }

            .screen-preview-popup--portrait {
                width: min(48vw, 51.75vh);
                height: auto;
                max-width: 92vw;
                max-height: 92vh;
                aspect-ratio: 9 / 16;
            }

            .screen-preview-overlay:fullscreen,
            .screen-preview-overlay:-webkit-full-screen {
                width: 100vw;
                height: 100vh;
                padding: 24px;
                background: rgba(15, 23, 42, 0.92);
            }

            .screen-preview-overlay--dark:fullscreen,
            .screen-preview-overlay--dark:-webkit-full-screen {
                background: rgba(0, 0, 0, 0.92);
            }

            .screen-preview-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                flex: 0 0 auto;
                min-height: 52px;
                padding: 10px 14px;
                background: #ffffff;
                border-bottom: 1px solid #e2e8f0;
            }

            .screen-preview-title {
                margin: 0;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: #0f172a;
                font-size: 15px;
                font-weight: 700;
            }

            .screen-preview-actions {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                flex-shrink: 0;
            }

            .screen-preview-btn {
                width: 34px;
                height: 34px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border: 1px solid #e2e8f0;
                border-radius: 10px;
                background: #ffffff;
                color: #334155;
                cursor: pointer;
                transition: background .18s ease, border-color .18s ease, color .18s ease, transform .12s ease;
            }

            .screen-preview-btn:hover {
                background: #f8fafc;
                border-color: #cbd5e1;
                color: #ff007f;
            }

            .screen-preview-btn:active {
                transform: scale(.96);
            }

            .screen-preview-frame {
                flex: 1 1 auto;
                width: 100%;
                min-height: 0;
                border: 0;
                background: #000000;
            }

            .screen-preview-overlay--dark {
                background: rgba(0, 0, 0, 0.72);
            }

            .screen-preview-overlay--dark .screen-preview-popup {
                background: #111217;
                border-color: rgba(226, 232, 240, 0.34);
                box-shadow: 0 28px 80px rgba(0, 0, 0, 0.65);
            }

            .screen-preview-overlay--dark .screen-preview-header {
                background: #18191f;
                border-color: #2c2f38;
            }

            .screen-preview-overlay--dark .screen-preview-title {
                color: #f8fafc;
            }

            .screen-preview-overlay--dark .screen-preview-btn {
                background: #1f2128;
                border-color: #343844;
                color: #f8fafc;
            }

            .screen-preview-overlay--dark .screen-preview-btn:hover {
                background: rgba(255, 0, 127, 0.14);
                border-color: rgba(255, 0, 127, 0.44);
                color: #ff4a9b;
            }

            @media (max-width: 768px) {
                .screen-preview-overlay {
                    padding: 12px;
                }

                .screen-preview-popup--landscape,
                .screen-preview-popup--portrait {
                    width: 96vw;
                    max-height: 92vh;
                }
            }
        `;
        overlay.appendChild(style);

        const popup = document.createElement('div');
        popup.className = `screen-preview-popup ${isLandscape ? 'screen-preview-popup--landscape' : 'screen-preview-popup--portrait'}`;

        const titleBar = document.createElement('div');
        titleBar.className = 'screen-preview-header';

        const title = document.createElement('h2');
        title.className = 'screen-preview-title';
        title.textContent = `Screen Preview (${isLandscape ? '16:9' : '9:16'})`;

        const buttonsContainer = document.createElement('div');
        buttonsContainer.className = 'screen-preview-actions';

        const fullscreenBtn = document.createElement('button');
        fullscreenBtn.className = 'screen-preview-btn';
        fullscreenBtn.title = 'Full screen';
        fullscreenBtn.setAttribute('aria-label', 'Full screen');
        fullscreenBtn.innerHTML = `
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M21 16v3a2 2 0 0 1-2 2h-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;
        fullscreenBtn.onclick = () => {
            const isFullscreen =
                document.fullscreenElement === overlay ||
                (document as any).webkitFullscreenElement === overlay ||
                (document as any).mozFullScreenElement === overlay ||
                (document as any).msFullscreenElement === overlay;

            if (isFullscreen) {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if ((document as any).webkitExitFullscreen) {
                    (document as any).webkitExitFullscreen();
                } else if ((document as any).mozCancelFullScreen) {
                    (document as any).mozCancelFullScreen();
                } else if ((document as any).msExitFullscreen) {
                    (document as any).msExitFullscreen();
                }
            } else if (overlay.requestFullscreen) {
                overlay.requestFullscreen();
            } else if ((overlay as any).webkitRequestFullscreen) {
                (overlay as any).webkitRequestFullscreen();
            } else if ((overlay as any).mozRequestFullScreen) {
                (overlay as any).mozRequestFullScreen();
            } else if ((overlay as any).msRequestFullscreen) {
                (overlay as any).msRequestFullscreen();
            }
        };

        const closeBtn = document.createElement('button');
        closeBtn.className = 'screen-preview-btn';
        closeBtn.title = 'Close';
        closeBtn.setAttribute('aria-label', 'Close preview');
        closeBtn.innerHTML = `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;
        closeBtn.onclick = () => document.body.removeChild(overlay);

        buttonsContainer.appendChild(fullscreenBtn);
        buttonsContainer.appendChild(closeBtn);

        titleBar.appendChild(title);
        titleBar.appendChild(buttonsContainer);

        const iframe = document.createElement('iframe');
        iframe.className = 'screen-preview-frame';
        iframe.src = url;
        iframe.setAttribute('allowfullscreen', 'true');

        popup.appendChild(titleBar);
        popup.appendChild(iframe);
        overlay.appendChild(popup);

        document.body.appendChild(overlay);
    }

    static openMediaPreviewPopup(url: string, fileName: string, fileFormat: string) {
        // Create overlay
        const overlay = document.createElement('div');
        const isDarkMode = document.body.classList.contains('dark-mode');
        overlay.className = `preview-overlay${isDarkMode ? ' preview-overlay--dark' : ''}`;

        // Add scoped styling
        const style = document.createElement('style');
        style.innerHTML = `
            .preview-overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background: rgba(15, 23, 42, 0.35);
                backdrop-filter: blur(8px);
                -webkit-backdrop-filter: blur(8px);
                z-index: 10000;
                display: flex;
                justify-content: center;
                align-items: center;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            }
            .preview-popup {
                position: relative;
                background: #ffffff;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
                width: 85%;
                height: 90%;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }
            .preview-popup:fullscreen {
                width: 100vw !important;
                height: 100vh !important;
                border: none !important;
                border-radius: 0 !important;
                background: #000000 !important;
            }
            .preview-popup:-webkit-full-screen {
                width: 100vw !important;
                height: 100vh !important;
                border: none !important;
                border-radius: 0 !important;
                background: #000000 !important;
            }
            .preview-popup:fullscreen .preview-header {
                display: none !important;
            }
            .preview-popup:-webkit-full-screen .preview-header {
                display: none !important;
            }
            .preview-popup:fullscreen .preview-media-content {
                background: #000000 !important;
                padding: 0 !important;
                width: 100% !important;
                height: 100% !important;
            }
            .preview-popup:-webkit-full-screen .preview-media-content {
                background: #000000 !important;
                padding: 0 !important;
                width: 100% !important;
                height: 100% !important;
            }
            .preview-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                background: #ffffff;
                padding: 14px 24px;
                border-bottom: 1px solid #e2e8f0;
                color: #0f172a;
            }
            .preview-title {
                font-size: 15px;
                font-weight: 600;
                letter-spacing: -0.01em;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 40%;
                color: #0f172a;
                display: flex;
                align-items: center;
                gap: 8px;
            }
            .preview-controls-container {
                display: flex;
                align-items: center;
                gap: 12px;
            }
            .zoom-control-pill {
                display: flex;
                align-items: center;
                background: #f1f5f9;
                border: 1px solid #cbd5e1;
                border-radius: 30px;
                padding: 2px 4px;
                gap: 2px;
            }
            .preview-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: none;
                background: transparent;
                color: #475569;
                cursor: pointer;
                transition: all 0.15s ease;
            }
            .preview-btn:hover {
                background: #e2e8f0;
                color: #0f172a;
            }
            .preview-btn:active {
                transform: scale(0.92);
            }
            .zoom-btn {
                width: 28px;
                height: 28px;
            }
            .zoom-level-btn {
                font-size: 12px;
                font-weight: 600;
                color: #334155;
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 4px 8px;
                border-radius: 12px;
                min-width: 50px;
                text-align: center;
                transition: all 0.15s ease;
            }
            .zoom-level-btn:hover {
                background: #e2e8f0;
                color: #0f172a;
            }
            .action-divider {
                width: 1px;
                height: 20px;
                background: #cbd5e1;
            }
            .preview-close-btn {
                color: #ef4444 !important;
            }
            .preview-close-btn:hover {
                background: #fee2e2 !important;
                color: #dc2626 !important;
            }
            .preview-media-content {
                flex: 1;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 24px;
                overflow: auto;
                background: #f8fafc;
            }
            .preview-media-content img, .preview-media-content iframe {
                box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
            }
            .preview-popup:fullscreen .preview-media-content img, 
            .preview-popup:fullscreen .preview-media-content iframe,
            .preview-popup:-webkit-full-screen .preview-media-content img,
            .preview-popup:-webkit-full-screen .preview-media-content iframe {
                box-shadow: none !important;
            }
            .fullscreen-floating-controls {
                display: none;
                position: absolute;
                top: 16px;
                right: 16px;
                gap: 8px;
                z-index: 100000;
            }
            .preview-popup:fullscreen .fullscreen-floating-controls {
                display: flex !important;
            }
            .preview-popup:-webkit-full-screen .fullscreen-floating-controls {
                display: flex !important;
            }
            .floating-btn {
                background: rgba(15, 23, 42, 0.6) !important;
                color: #ffffff !important;
                border: 1px solid rgba(255, 255, 255, 0.15) !important;
                backdrop-filter: blur(4px) !important;
                -webkit-backdrop-filter: blur(4px) !important;
            }
            .floating-btn:hover {
                background: rgba(15, 23, 42, 0.85) !important;
                color: #ffffff !important;
            }
            .preview-overlay--dark {
                background: rgba(0, 0, 0, 0.68);
            }
            .preview-overlay--dark .preview-popup {
                background: #17181d;
                border-color: #535660;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6);
            }
            .preview-overlay--dark .preview-header {
                background: #1d1f25;
                border-bottom-color: #353841;
                color: #f4f4f5;
            }
            .preview-overlay--dark .preview-title {
                color: #f4f4f5;
            }
            .preview-overlay--dark .zoom-control-pill {
                background: #282a31;
                border-color: #4a4d57;
            }
            .preview-overlay--dark .preview-btn,
            .preview-overlay--dark .zoom-level-btn {
                color: #d4d4d8;
            }
            .preview-overlay--dark .preview-btn:hover,
            .preview-overlay--dark .zoom-level-btn:hover {
                background: #383b45;
                color: #ffffff;
            }
            .preview-overlay--dark .action-divider {
                background: #4a4d57;
            }
            .preview-overlay--dark .preview-close-btn:hover {
                background: rgba(239, 68, 68, 0.16) !important;
            }
            .preview-overlay--dark .preview-media-content {
                background: #111216;
            }
        `;
        overlay.appendChild(style);

        // Create popup container
        const popup = document.createElement('div');
        popup.className = 'preview-popup';

        // Title Bar
        const titleBar = document.createElement('div');
        titleBar.className = 'preview-header';

        const title = document.createElement('span');
        title.className = 'preview-title';
        title.textContent = 'Preview - ' + fileName;

        const buttonsContainer = document.createElement('div');
        buttonsContainer.className = 'preview-controls-container';

        // Download Button
        const downloadBtn = document.createElement('button');
        downloadBtn.className = 'preview-btn';
        downloadBtn.title = 'Download';
        downloadBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        `;
        downloadBtn.onclick = () => {
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        const enterFsSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>`;
        const exitFsSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14h6v6m10-6h-6v6M4 10h6V4m10 6h-6V4"/></svg>`;

        // Fullscreen Button
        const fullscreenBtn = document.createElement('button');
        fullscreenBtn.className = 'preview-btn';
        fullscreenBtn.title = 'Full screen';
        fullscreenBtn.innerHTML = enterFsSvg;

        const updateFsIcon = () => {
            const isFullscreen =
                document.fullscreenElement === popup ||
                (document as any).webkitFullscreenElement === popup ||
                (document as any).mozFullScreenElement === popup ||
                (document as any).msFullscreenElement === popup;

            if (isFullscreen) {
                fullscreenBtn.innerHTML = exitFsSvg;
                fullscreenBtn.title = 'Exit full screen';
            } else {
                fullscreenBtn.innerHTML = enterFsSvg;
                fullscreenBtn.title = 'Full screen';
            }
        };

        // Listen for fullscreen change event to update icon
        document.addEventListener('fullscreenchange', updateFsIcon);
        document.addEventListener('webkitfullscreenchange', updateFsIcon);
        document.addEventListener('mozfullscreenchange', updateFsIcon);
        document.addEventListener('MSFullscreenChange', updateFsIcon);

        fullscreenBtn.onclick = () => {
            const isFullscreen =
                document.fullscreenElement === popup ||
                (document as any).webkitFullscreenElement === popup ||
                (document as any).mozFullScreenElement === popup ||
                (document as any).msFullscreenElement === popup;

            if (isFullscreen) {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if ((document as any).webkitExitFullscreen) {
                    (document as any).webkitExitFullscreen();
                } else if ((document as any).mozCancelFullScreen) {
                    (document as any).mozCancelFullScreen();
                } else if ((document as any).msExitFullscreen) {
                    (document as any).msExitFullscreen();
                }
            } else {
                if (popup.requestFullscreen) {
                    popup.requestFullscreen();
                } else if ((popup as any).webkitRequestFullscreen) {
                    (popup as any).webkitRequestFullscreen();
                } else if ((popup as any).mozRequestFullScreen) {
                    (popup as any).mozRequestFullScreen();
                } else if ((popup as any).msRequestFullscreen) {
                    (popup as any).msRequestFullscreen();
                }
            }
        };

        // Close Button (with cleanup)
        const closeBtn = document.createElement('button');
        closeBtn.className = 'preview-btn preview-close-btn';
        closeBtn.title = 'Close';
        closeBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        `;
        
        const cleanup = () => {
            document.removeEventListener('fullscreenchange', updateFsIcon);
            document.removeEventListener('webkitfullscreenchange', updateFsIcon);
            document.removeEventListener('mozfullscreenchange', updateFsIcon);
            document.removeEventListener('MSFullscreenChange', updateFsIcon);

            const isFullscreen =
                document.fullscreenElement === popup ||
                (document as any).webkitFullscreenElement === popup ||
                (document as any).mozFullScreenElement === popup ||
                (document as any).msFullscreenElement === popup;

            if (isFullscreen) {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if ((document as any).webkitExitFullscreen) {
                    (document as any).webkitExitFullscreen();
                }
            }

            document.body.removeChild(overlay);
        };
        closeBtn.onclick = cleanup;

        // Floating fullscreen controls (for full black signage theater)
        const floatControls = document.createElement('div');
        floatControls.className = 'fullscreen-floating-controls';

        const floatExitFsBtn = document.createElement('button');
        floatExitFsBtn.className = 'preview-btn floating-btn';
        floatExitFsBtn.title = 'Exit full screen';
        floatExitFsBtn.innerHTML = exitFsSvg;
        floatExitFsBtn.onclick = () => {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            }
        };

        const floatCloseBtn = document.createElement('button');
        floatCloseBtn.className = 'preview-btn floating-btn preview-close-btn';
        floatCloseBtn.title = 'Close';
        floatCloseBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        `;
        floatCloseBtn.onclick = cleanup;

        floatControls.appendChild(floatExitFsBtn);
        floatControls.appendChild(floatCloseBtn);
        popup.appendChild(floatControls);

        let zoom = 100;
        let panX = 0;
        let panY = 0;
        let isDragging = false;
        let startX = 0;
        let startY = 0;

        const MIN_ZOOM = 10;
        const MAX_ZOOM = 1000;
        const ZOOM_STEP = 10;

        const zoomContainer = document.createElement('div');
        zoomContainer.className = 'zoom-control-pill';

        const zoomOutBtn = document.createElement('button');
        zoomOutBtn.className = 'preview-btn zoom-btn';
        zoomOutBtn.title = 'Zoom out';
        zoomOutBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
        `;

        const zoomResetBtn = document.createElement('button');
        zoomResetBtn.className = 'zoom-level-btn';
        zoomResetBtn.title = 'Reset to 100%';
        zoomResetBtn.textContent = '100%';

        const zoomInBtn = document.createElement('button');
        zoomInBtn.className = 'preview-btn zoom-btn';
        zoomInBtn.title = 'Zoom in';
        zoomInBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        `;

        zoomContainer.appendChild(zoomOutBtn);
        zoomContainer.appendChild(zoomResetBtn);
        zoomContainer.appendChild(zoomInBtn);

        // Append buttons
        const isImage = ['.jpg', '.jpeg', '.png', '.gif'].includes(fileFormat.toLowerCase());
        const isHtml = ['.html', 'html', 'iframe'].includes(fileFormat.toLowerCase());

        if (isImage) {
            buttonsContainer.appendChild(zoomContainer);
            const divider = document.createElement('div');
            divider.className = 'action-divider';
            buttonsContainer.appendChild(divider);
        }
        buttonsContainer.appendChild(fullscreenBtn);
        // if (!isHtml) {
        //     buttonsContainer.appendChild(downloadBtn);
        // }
        buttonsContainer.appendChild(closeBtn);

        const updateZoom = () => {
            if (!mediaElement || mediaElement instanceof HTMLVideoElement) {
                return;
            }

            mediaElement.style.transform = 'translate(' + panX + 'px, ' + panY + 'px) scale(' + (zoom / 100) + ')';
            mediaElement.style.transformOrigin = 'center center';

            zoomResetBtn.textContent = zoom + '%';
        };

        // Append title
        titleBar.appendChild(title);
        titleBar.appendChild(buttonsContainer);

        // Create media element
        const contentContainer = document.createElement('div');
        contentContainer.className = 'preview-media-content';

        const lowerFormat = fileFormat.toLowerCase();
        let mediaElement: HTMLElement;

        zoomInBtn.onclick = () => {
            zoom = Math.min(MAX_ZOOM, zoom + ZOOM_STEP);
            updateZoom();
        };

        zoomOutBtn.onclick = () => {
            zoom = Math.max(MIN_ZOOM, zoom - ZOOM_STEP);
            updateZoom();
        };

        zoomResetBtn.onclick = () => {
            zoom = 100;
            panX = 0;
            panY = 0;
            updateZoom();
        };

        if (lowerFormat === '.jpg' || lowerFormat === '.jpeg' || lowerFormat === '.png' || lowerFormat === '.gif') {
            const img = document.createElement('img');
            img.src = url;
            img.style.maxWidth = '100%';
            img.style.maxHeight = '100%';
            img.style.transition = 'transform .15s ease';
            img.style.transformOrigin = 'center center';
            img.style.cursor = 'grab';
            img.style.borderRadius = '4px';
            mediaElement = img;
        } else if (lowerFormat === '.mp4' || lowerFormat === '.webm' || lowerFormat === '.ogg') {
            const video = document.createElement('video');
            video.src = url;
            video.controls = true;
            video.style.maxWidth = '100%';
            video.style.maxHeight = '100%';
            video.style.borderRadius = '4px';
            mediaElement = video;
        } else if (lowerFormat === '.pdf' || lowerFormat === '.html' || lowerFormat === 'html') {
            const iframe = document.createElement('iframe');
            iframe.src = url;
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.transition = 'transform .15s ease';
            iframe.style.transformOrigin = 'center center';
            iframe.style.border = 'none';
            iframe.setAttribute('allowfullscreen', 'true');
            mediaElement = iframe;
        } else {
            const unsupported = document.createElement('p');
            unsupported.textContent = 'Preview not supported for this file type.';
            unsupported.style.textAlign = 'center';
            mediaElement = unsupported;
        }
        contentContainer.appendChild(mediaElement);

        contentContainer.addEventListener('mousedown', (e: MouseEvent) => {
            if (!mediaElement || mediaElement instanceof HTMLVideoElement) return;
            e.preventDefault(); // Prevent native browser drag
            isDragging = true;
            startX = e.clientX - panX;
            startY = e.clientY - panY;
            mediaElement.style.cursor = 'grabbing';
        });

        contentContainer.addEventListener('mousemove', (e: MouseEvent) => {
            if (!isDragging) return;
            panX = e.clientX - startX;
            panY = e.clientY - startY;
            updateZoom();
        });

        contentContainer.addEventListener('mouseup', () => {
            isDragging = false;
            if (mediaElement && !(mediaElement instanceof HTMLVideoElement)) {
                mediaElement.style.cursor = 'grab';
            }
        });

        contentContainer.addEventListener('mouseleave', () => {
            isDragging = false;
            if (mediaElement && !(mediaElement instanceof HTMLVideoElement)) {
                mediaElement.style.cursor = 'grab';
            }
        });

        contentContainer.addEventListener(
            'wheel',
            (event: WheelEvent) => {
                if (!mediaElement || mediaElement instanceof HTMLVideoElement) {
                    return;
                }

                event.preventDefault();

                if (event.deltaY < 0) {
                    zoom += ZOOM_STEP;
                } else {
                    zoom -= ZOOM_STEP;
                }

                zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
                updateZoom();
            },
            { passive: false }
        );

        // Assemble and display
        popup.appendChild(titleBar);
        popup.appendChild(contentContainer);
        overlay.appendChild(popup);
        document.body.appendChild(overlay);
    }    static getThumbnail(file: any): string {

        if (!file) return 'svg/files_icon.svg';

        const format = (file.file_format || '').toLowerCase();
        const cleanFormat = format.replace('.', '');
        const appType = (file.app_type || '').toLowerCase();
        const category = (file.category || '').toLowerCase();

        // ✅ PDF
        if (cleanFormat === 'pdf') {
            return file.thumbnail || 'svg/pdf_icon.svg';
        }

        // ✅ App
        if (category === 'app' && cleanFormat === 'html') {
            return appType
                ? 'svg/apps/' + appType.replace(/\s+/g, '').toLowerCase() + '.svg'
                : 'svg/apps/default.svg';
        }

        // ✅ DzynFlix Template
        if (category === 'template' && cleanFormat === 'html') {
            return file.thumbnail || 'images/dzynflix_thumbnail.png';
        }

        // ✅ Creative Template
        if (category === 'creative-template' && cleanFormat === 'html') {
            return file.thumbnail || 'images/templates_thumbnail.png';
        }

        // ✅ Image
        if (category === 'image') {
            return file.thumbnail || 'svg/image_icon.svg';
        }

        // ✅ Video
        if (category === 'video') {
            return file.thumbnail || 'svg/video_icon.svg';
        }

        // ✅ SVG / fallback app
        if ((file.file_format === Constants.fileFormats.app && file.app_type === 'default') ||
            Constants.fileFormats.svg.includes(file.file_format)) {
            return file.thumbnail || 'images/dzynflix_thumbnail.png';
        }

        // ✅ Generic app fallback
        if (file.file_format === Constants.fileFormats.app) {
            return file.app_type
                ? 'svg/apps/' + file.app_type.replace(/\s+/g, '').toLowerCase() + '.svg'
                : 'svg/files_icon.svg';
        }

        return file.thumbnail || 'svg/files_icon.svg';
    }


}
